import { useState } from 'react';
import cn from 'classnames';
import { Button } from '@Components/Button';
import { Dropdown, DropdownOption } from '@Components/Dropdown';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { useSetError } from '@src/redux/modals';
import { useExperimentFlag } from '@src/utils/experiment-context';
import {
  ContentMetaDataFields,
  GlobalContent,
  StreamingNetworkRecord
} from './streaming-networks-constants';
import { joinPublisherGenre, parsePublisherGenre } from './streaming-networks-utils';
import { saveNetworkContentMetaData } from './use-streaming-networks';

interface NetworkContentDetailsTabProps {
  network: StreamingNetworkRecord;
  onRefetch: () => Promise<unknown>;
}

interface SelectedContent {
  contentValue: string;
  platformPrefixValue: string;
}

const FIELD_DEFAULTS: ContentMetaDataFields = {
  genre: null,
  publisherGenre: null,
  placementGenre: null,
  format: null,
  pmp: null,
  guaranteed: null,
  fepOlv: null,
  enabled: null
};

// The real, backend-authoritative option lists -- these are the actual values already stored on
// existing networks, not a taxonomy this page gets to invent. Genre in particular was never built
// out beyond "None" -- that's carried over as-is rather than padded out with invented values.
const FORMAT_OPTIONS: DropdownOption[] = [
  { label: 'OLV', value: 'olv' },
  { label: 'FEP', value: 'fep' },
  { label: 'Music', value: 'music' },
  { label: 'Podcast', value: 'podcast' },
  { label: 'Display', value: 'display' },
  { label: 'None', value: 'none' }
];
const GENRE_OPTIONS: DropdownOption[] = [{ label: 'None', value: 'none' }];
const PUBLISHER_GENRE_OPTIONS: DropdownOption[] = [
  { label: 'Audience', value: 'audience' },
  { label: 'Gaming', value: 'gaming' },
  { label: 'Openmarket', value: 'openmarket' },
  { label: 'Talk', value: 'talk' },
  { label: 'Documentary', value: 'documentary' },
  { label: 'Business/Finance', value: 'business_finance' },
  { label: 'Family/Kids', value: 'family_kids' },
  { label: 'Hispanic', value: 'hispanic' },
  { label: 'Generic Audio', value: 'generic_audio' },
  { label: 'News', value: 'news' },
  { label: 'Music', value: 'music' },
  { label: 'Sports', value: 'sports' },
  { label: 'Entertainment', value: 'entertainment' },
  { label: 'Generic Video', value: 'generic_video' },
  { label: 'Lifestyle Food', value: 'lifestyle_food' },
  { label: 'Crime', value: 'crime' },
  { label: 'Other', value: 'other' },
  { label: 'Technology', value: 'technology' },
  { label: 'None', value: 'none' }
];
const PLACEMENT_GENRE_OPTIONS: DropdownOption[] = [
  { label: 'Animation', value: 'animation' },
  { label: 'HealthWellness', value: 'health_wellness' },
  { label: 'Technology', value: 'technology' },
  { label: 'Comedy', value: 'comedy' },
  { label: 'Documentary', value: 'documentary' },
  { label: 'PopCulture', value: 'pop_culture' },
  { label: 'Crime', value: 'crime' },
  { label: 'Audience', value: 'audience' },
  { label: 'Sports', value: 'sports' },
  { label: 'Entertainment', value: 'entertainment' },
  { label: 'Lifestyle/Food', value: 'lifestyle_food' },
  { label: 'Reality', value: 'reality' },
  { label: 'Drama', value: 'drama' },
  { label: 'Business/Finance', value: 'business_finance' },
  { label: 'Family/Kids', value: 'family_kids' },
  { label: 'Action', value: 'action' },
  { label: 'Music', value: 'music' },
  { label: 'None', value: 'none' }
];
const FEP_OLV_OPTIONS: DropdownOption[] = [
  { label: 'SFV', value: 'SFV' },
  { label: 'FEP', value: 'FEP' },
  { label: 'AUD', value: 'AUD' },
  { label: 'OLV', value: 'OLV' },
  { label: 'NONE', value: 'NONE' }
];
const BOOLEAN_OPTIONS = [
  { label: 'True', value: 'true' },
  { label: 'False', value: 'false' }
] as const;

const contentMapKey = (content: SelectedContent): string =>
  `${content.contentValue}_${content.platformPrefixValue}`;

export const NetworkContentDetailsTab = ({ network, onRefetch }: NetworkContentDetailsTabProps) => {
  const setError = useSetError();
  // Only users with this flag on can ever enter edit mode here — everyone else sees a read-only form.
  const canManageContent = Boolean(useExperimentFlag('enableStreamingNetworksUpdates'));
  const [selected, setSelected] = useState<SelectedContent>();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ContentMetaDataFields>();
  const [saving, setSaving] = useState(false);

  const visibleContent = network.globalContent.filter(content => !content.deleted);
  const metaData = selected ? network.contentMetaDataMap[contentMapKey(selected)] : undefined;
  const originalFields: ContentMetaDataFields = metaData
    ? {
        genre: metaData.genre,
        publisherGenre: metaData.publisherGenre,
        placementGenre: metaData.placementGenre,
        format: metaData.format,
        pmp: metaData.pmp,
        guaranteed: metaData.guaranteed,
        fepOlv: metaData.fepOlv,
        enabled: metaData.enabled
      }
    : FIELD_DEFAULTS;
  const displayedFields = editing && draft ? draft : originalFields;

  const selectContent = (content: SelectedContent) => {
    setSelected(content);
    setEditing(false);
    setDraft(undefined);
  };

  const startEditing = () => {
    setDraft(originalFields);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraft(undefined);
  };

  const updateDraftField = <K extends keyof ContentMetaDataFields>(
    field: K,
    value: ContentMetaDataFields[K]
  ) => setDraft(current => ({ ...(current ?? originalFields), [field]: value }));

  const handleSave = async () => {
    if (!selected || !draft) return;

    const hasChanges = (Object.keys(draft) as (keyof ContentMetaDataFields)[]).some(
      field => draft[field] !== originalFields[field]
    );
    if (!hasChanges) {
      setEditing(false);
      return;
    }

    setSaving(true);
    try {
      await saveNetworkContentMetaData(
        network.shortCode,
        selected.contentValue,
        selected.platformPrefixValue,
        draft
      );
    } catch (e) {
      setError({ message: `Couldn't save content metadata: ${e.message}`, reportError: e });
      setSaving(false);
      return;
    }

    try {
      await onRefetch();
      setEditing(false);
    } catch (e) {
      setError({
        message: `Saved, but couldn't refresh content metadata: ${e.message}`,
        reportError: e
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='sn-content-details'>
      <ContentValueList items={visibleContent} selected={selected} onSelect={selectContent} />
      <ContentMetaDataPanel
        selected={selected}
        fields={displayedFields}
        editing={editing}
        saving={saving}
        canManageContent={canManageContent}
        onFieldChange={updateDraftField}
        onStartEditing={startEditing}
        onSave={handleSave}
        onCancel={cancelEditing}
      />
    </div>
  );
};

interface ContentValueListProps {
  items: GlobalContent[];
  selected?: SelectedContent;
  onSelect: (content: SelectedContent) => void;
}

const ContentValueList = ({ items, selected, onSelect }: ContentValueListProps) => {
  if (items.length === 0) {
    return (
      <div className='sn-content-details-list sn-empty'>
        There are currently no content values for this network.
      </div>
    );
  }

  return (
    <div className='sn-content-details-list'>
      {items.map(content => (
        <InteractiveDiv
          key={contentMapKey(content)}
          className={cn('sn-content-details-item', {
            active:
              selected?.contentValue === content.contentValue &&
              selected?.platformPrefixValue === content.platformPrefixValue
          })}
          onClick={() =>
            onSelect({
              contentValue: content.contentValue,
              platformPrefixValue: content.platformPrefixValue
            })
          }
        >
          <span className='sn-content-details-item-value'>{content.contentValue}</span>
          <span className='sn-content-details-item-prefix'>{content.platformPrefixValue}</span>
        </InteractiveDiv>
      ))}
    </div>
  );
};

interface ContentMetaDataPanelProps {
  selected?: SelectedContent;
  fields: ContentMetaDataFields;
  editing: boolean;
  saving: boolean;
  canManageContent: boolean;
  onFieldChange: <K extends keyof ContentMetaDataFields>(
    field: K,
    value: ContentMetaDataFields[K]
  ) => void;
  onStartEditing: () => void;
  onSave: () => void;
  onCancel: () => void;
}

const ContentMetaDataPanel = ({
  selected,
  fields,
  editing,
  saving,
  canManageContent,
  onFieldChange,
  onStartEditing,
  onSave,
  onCancel
}: ContentMetaDataPanelProps) => {
  if (!selected) {
    return (
      <div className='sn-content-details-panel sn-empty'>
        Select a row on the left to edit metadata
      </div>
    );
  }

  return (
    <div className='sn-content-details-panel'>
      <div className='sn-content-details-panel-head'>
        <span className='sn-content-details-panel-title'>{selected.contentValue}</span>
        {canManageContent && !editing && <Button onClick={onStartEditing}>Edit</Button>}
        {canManageContent && editing && (
          <div className='sn-content-details-panel-actions'>
            <Button disabled={saving} onClick={onCancel}>
              Cancel
            </Button>
            <Button loading={saving} onClick={onSave}>
              Save
            </Button>
          </div>
        )}
      </div>
      <div className='sn-content-details-fields'>
        <StringFieldRow
          label='Genre'
          value={fields.genre}
          options={GENRE_OPTIONS}
          editing={editing}
          onChange={value => onFieldChange('genre', value)}
        />
        <MultiStringFieldRow
          label='Publisher Genre'
          value={fields.publisherGenre}
          options={PUBLISHER_GENRE_OPTIONS}
          editing={editing}
          onChange={value => onFieldChange('publisherGenre', value)}
        />
        <StringFieldRow
          label='Placement Genre'
          value={fields.placementGenre}
          options={PLACEMENT_GENRE_OPTIONS}
          editing={editing}
          onChange={value => onFieldChange('placementGenre', value)}
        />
        <StringFieldRow
          label='Format'
          value={fields.format}
          options={FORMAT_OPTIONS}
          editing={editing}
          onChange={value => onFieldChange('format', value)}
        />
        <StringFieldRow
          label='FEP/OLV'
          value={fields.fepOlv}
          options={FEP_OLV_OPTIONS}
          editing={editing}
          onChange={value => onFieldChange('fepOlv', value)}
        />
        <BooleanFieldRow
          label='PMP'
          value={fields.pmp}
          editing={editing}
          onChange={value => onFieldChange('pmp', value)}
        />
        <BooleanFieldRow
          label='Guaranteed'
          value={fields.guaranteed}
          editing={editing}
          onChange={value => onFieldChange('guaranteed', value)}
        />
        <BooleanFieldRow
          label='Enabled'
          value={fields.enabled}
          editing={editing}
          onChange={value => onFieldChange('enabled', value)}
        />
      </div>
    </div>
  );
};

interface StringFieldRowProps {
  label: string;
  value: string | null;
  options: DropdownOption[];
  editing: boolean;
  onChange: (value: string | null) => void;
}

const StringFieldRow = ({ label, value, options, editing, onChange }: StringFieldRowProps) => (
  <div className='sn-content-details-field'>
    <span className='sn-content-details-field-label'>{label}</span>
    <div className='sn-content-details-field-value'>
      <Dropdown<string>
        clearable
        disabled={!editing}
        options={options}
        value={value ?? undefined}
        placeholder={editing ? 'Select…' : '—'}
        onChange={onChange}
        onClear={() => onChange(null)}
      />
    </div>
  </div>
);

const MultiStringFieldRow = ({ label, value, options, editing, onChange }: StringFieldRowProps) => (
  <div className='sn-content-details-field'>
    <span className='sn-content-details-field-label'>{label}</span>
    <div className='sn-content-details-field-value sn-content-details-field-value-wide'>
      <Dropdown<string>
        multiSelect
        searchable
        showChips
        disabled={!editing}
        options={options}
        value={parsePublisherGenre(value)}
        placeholder={editing ? 'Select…' : '—'}
        onChange={selected => onChange(joinPublisherGenre(selected))}
      />
    </div>
  </div>
);

interface BooleanFieldRowProps {
  label: string;
  value: boolean | null;
  editing: boolean;
  onChange: (value: boolean | null) => void;
}

const booleanToDropdownValue = (value: boolean | null): 'true' | 'false' | undefined => {
  if (value === null) return undefined;
  return value ? 'true' : 'false';
};

const BooleanFieldRow = ({ label, value, editing, onChange }: BooleanFieldRowProps) => (
  <div className='sn-content-details-field'>
    <span className='sn-content-details-field-label'>{label}</span>
    <div className='sn-content-details-field-value'>
      <Dropdown<'true' | 'false'>
        clearable
        disabled={!editing}
        options={BOOLEAN_OPTIONS}
        value={booleanToDropdownValue(value)}
        placeholder={editing ? 'Select…' : '—'}
        onChange={selectedValue => onChange(selectedValue === 'true')}
        onClear={() => onChange(null)}
      />
    </div>
  </div>
);
